import spartacusOnboardingAPI from '@api/jooicez-backend/spartacus/onboarding';
import Iconify from '@components/iconify';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import icons from '@utils/icons';
import { useState } from 'react';
import { useApp } from 'src/contexts/ApplicationContext';
import { useResponsive } from 'src/hooks/use-responsive';

import PreferenceCardGrid from '../components/PreferenceCardGrid';

import { StepProps } from './steps';

const OPTIONS = [
  { key: 'bring_back', label: 'Bring back patient more often', icon: icons.commons.userCirclePlus },
  { key: 'word_of_mouth', label: 'Turn patient into word of mouth', icon: icons.navbar.listStar },
  { key: 'fill_schedule', label: 'Fill your schedule consistently', icon: icons.commons.calendarDots },
  { key: 'local_presence', label: 'Build a stronger local presence', icon: icons.navbar.mapPinArea },
  { key: 'create_content', label: 'Create content without the effort', icon: icons.commons.press },
] as const;

export default function Preference1Step({ onNext }: StepProps) {
  const { organizationId } = useApp();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useResponsive('down', 'sm');

  const toggleOption = (key: string) => {
    setSelectedOptions((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleContinue = async () => {
    if (!organizationId) return;
    setIsLoading(true);
    try {
      await Promise.all(
        selectedOptions.map((key) => {
          const option = OPTIONS.find((o) => o.key === key);
          if (!option) return Promise.resolve();
          return spartacusOnboardingAPI.createSpecialization(organizationId, {
            key: option.key,
            content: option.label,
          });
        }),
      );
      onNext();
    } catch (error) {
      console.error('Error saving preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', sm: 'center' },
        pt: { xs: 4, sm: 0 },
        gap: 4,
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: 600 }}>
        <Typography variant={isMobile ? 'h2' : 'h1'} color="text.primary">
          <Box component="span" sx={{ color: 'secondary.darker' }}>
            What matter most
          </Box>{' '}
          to your practice?
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          We&apos;ll build the experience around your priority
        </Typography>
      </Box>

      <PreferenceCardGrid options={OPTIONS} selectedKeys={selectedOptions} onToggle={toggleOption} />

      <Button
        variant="contained"
        color="primary"
        size="large"
        disabled={selectedOptions.length === 0 || isLoading}
        onClick={handleContinue}
        endIcon={!isLoading ? <Iconify icon={icons.arrows.right} /> : undefined}
        sx={{ minWidth: { xs: '100%', sm: 200 } }}
      >
        {isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} color="inherit" />
            <span>Saving...</span>
          </Stack>
        ) : (
          'Continue'
        )}
      </Button>
    </Box>
  );
}
